import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  detectDeviceLanguage, 
  createTranslator, 
  normalizeLanguage,
  loadTranslations,
  type SupportedLanguage 
} from '@/lib/i18n';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => Promise<void>;
  t: (key: string, variables?: Record<string, any>) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'cashee-language';

const getInitialLanguage = (): SupportedLanguage => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return normalizeLanguage(stored);
  } catch (e) {
    console.warn('Failed to read language from localStorage:', e);
  }
  return detectDeviceLanguage();
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage);
  const [translator, setTranslator] = useState(() => createTranslator(getInitialLanguage()));
  const [isLoading, setIsLoading] = useState(true);
  const [translationsReady, setTranslationsReady] = useState(false);

  // Update <html lang>
  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  // Eagerly load translations for initial language on mount
  useEffect(() => {
    let cancelled = false;
    const initLang = getInitialLanguage();
    loadTranslations(initLang).then(() => {
      if (!cancelled) {
        setTranslator(() => createTranslator(initLang));
        setTranslationsReady(true);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Lazy-load translations when language changes
  useEffect(() => {
    if (!translationsReady) return; // Skip until initial load is done
    let cancelled = false;
    loadTranslations(language).then(() => {
      if (!cancelled) setTranslator(() => createTranslator(language));
    });
    return () => { cancelled = true; };
  }, [language, translationsReady]);

  // Load language from backend (if logged in)
  useEffect(() => {
    loadLanguageFromSupabase();
  }, []);

  const loadLanguageFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('budget_settings')
          .select('language')
          .eq('user_id', user.id)
          .single();
        
        if (data?.language) {
          const dbLang = normalizeLanguage(data.language);
          setLanguageState(dbLang);
          localStorage.setItem(STORAGE_KEY, dbLang);
        } else {
          await supabase
            .from('budget_settings')
            .upsert({ user_id: user.id, language: language, detected_language: language });
        }
      }
    } catch (error) {
      console.error('Failed to load language from backend:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setLanguage = useCallback(async (lang: SupportedLanguage) => {
    const normalizedLang = normalizeLanguage(lang);
    setLanguageState(normalizedLang);
    
    try { localStorage.setItem(STORAGE_KEY, normalizedLang); } catch {}

    // Eagerly load translations for the new language
    await loadTranslations(normalizedLang);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('budget_settings')
          .upsert({ user_id: user.id, language: normalizedLang });
      }
    } catch (error) {
      console.error('Failed to save language:', error);
    }
  }, []);

  const t = useCallback(
    (key: string, variables?: Record<string, any>) => translator(key, variables),
    [translator]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, isLoading }),
    [language, setLanguage, t, isLoading]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
